import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import LicensePlanTargetSelect from "@/v2-backoffice-client/license/plan/LicensePlanTargetSelect"
import LicensePlanMigrationStatusSelect from "@/v2-backoffice-client/license/plan/view/LicensePlanMigrationStatusSelect"
import { usePlanMigrationUpsertMutation } from "@/v2-backoffice-client/license/plan/view/license.plan.migration.upsert.mutation"
import { evictPlanMigrationQueries } from "@/v2-backoffice-client/license/license.utils"
import { PLAN_MIGRATION_STATUS_IN_DIALOGUE } from "@/v2-backoffice-client/license/license.constants"
import type { PlanMigrationsQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.migrations.query"

// Radix Select items can't carry an empty-string value, so "no target yet"
// gets a sentinel that is translated to null on submit.
const NO_TARGET_VALUE = "0"

export type LicensePlanMigrationDialogProps = {
  migration: PlanMigrationsQuery["getPlanMigrations"][number]
  /** The deprecated license being migrated FROM (the panel's license). */
  fromPlanId: number
  /** Seeds the target for fresh rows — the license's suggested successor. */
  suggestedToPlanId?: number | null
  open: boolean
  onOpenChange(open: boolean): void
}

// The migration row editor: where the renegotiation with ONE customer stands
// (status), what they agreed to move to (target), and the paper trail (note).
// The first save starts the conversation — the row leaves "Not started".
// Deliberately NOT where the license change happens: that is executed in
// Fenerum, and the row resolves itself once the mirror moves the license.
function LicensePlanMigrationDialog(props: LicensePlanMigrationDialogProps) {
  const { migration, fromPlanId, suggestedToPlanId, open, onOpenChange } = props

  const [statusCode, setStatusCode] = useState(
    String(
      migration.PlanMigrationStatusCode ?? PLAN_MIGRATION_STATUS_IN_DIALOGUE,
    ),
  )
  const [toPlanId, setToPlanId] = useState(
    migration.ToPlanId
      ? String(migration.ToPlanId)
      : suggestedToPlanId
        ? String(suggestedToPlanId)
        : NO_TARGET_VALUE,
  )
  const [note, setNote] = useState(migration.Note ?? "")

  const [upsertMigration, { loading, error }] = usePlanMigrationUpsertMutation({
    update: evictPlanMigrationQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Migration updated",
        description: `The migration state for ${migration.CustomerName ?? "the customer"} was saved.`,
      })
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            Migration —{" "}
            {migration.CustomerName ?? `License ${migration.LicenseId}`}
          </Dialog.Title>
          <Dialog.Description>
            Track the renegotiation here; execute the agreed change in Fenerum
            (typically a subscription change scheduled at renewal). The row
            resolves itself once the hourly mirror sees the license move.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <LicensePlanMigrationStatusSelect
              value={statusCode}
              onValueChange={setStatusCode}
            />
          </div>

          <div className="space-y-2">
            <Label>Agreed target license</Label>
            <LicensePlanTargetSelect
              value={toPlanId}
              onValueChange={setToPlanId}
              excludePlanId={fromPlanId}
              placeholder="What are they moving to?"
            >
              <Select.Item value={NO_TARGET_VALUE}>Not agreed yet</Select.Item>
            </LicensePlanTargetSelect>
          </div>

          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={note}
              rows={3}
              placeholder="What was said, offered, or agreed — the paper trail..."
              onChange={(event) => {
                setNote(event.target.value)
              }}
            />
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            data-feature-code="UI_BackofficeLicensePlan_Migration_Save"
            onClick={() => {
              void upsertMigration({
                variables: {
                  input: {
                    LicenseId: migration.LicenseId,
                    FromPlanId: fromPlanId,
                    PlanMigrationStatusCode: Number(statusCode),
                    ToPlanId:
                      toPlanId === NO_TARGET_VALUE ? null : Number(toPlanId),
                    Note: note.trim() ? note.trim() : null,
                  },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save Migration"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicensePlanMigrationDialog
